import { useEffect, useMemo, useState } from "react";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { db } from "./firebase";
import "./AuditLog.css";

const ACTION_LABELS = {
  "extra-income.create": "เพิ่มรายได้พิเศษ",
  "extra-income.update": "แก้รายได้พิเศษ",
  "owner-fee.update": "แก้ค่ากดของยย",
  "settlement.draft": "บันทึกร่างรอบโอน",
  "settlement.paid": "ยืนยันโอนเงิน",
  "settlement.update": "แก้รอบโอนย้อนหลัง",
  "press-fee.create": "บันทึกค่ากด",
  "press-fee.delete": "ลบรายการค่ากด",
};

const fmtDate = (value) => {
  const date = value?.toDate ? value.toDate() : value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("th-TH", {
    timeZone: "Asia/Bangkok", day: "2-digit", month: "short", year: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
};

const moneyMaybe = (value) => {
  if (typeof value !== "number") return value;
  return value.toLocaleString("th-TH", { maximumFractionDigits: 2 });
};

const describe = (log) => {
  const meta = log.meta || {};
  if (log.entityType === "extra-income") {
    const after = log.after || {};
    return `${after.teamName || meta.teamName || "ทีม"} · ${after.category || "รายได้พิเศษ"} · ${moneyMaybe(after.companyCostAmount || after.grossAmount || 0)} บาท`;
  }
  if (log.entityType === "press-fee") {
    const data = log.after || log.before || {};
    return `${data.teamName || meta.teamName || "ทีม"} · ${data.eventName || log.title || "ค่ากด"}${data.zone ? ` · ${data.zone}` : ""}`;
  }
  if (log.entityType === "settlement") {
    const data = log.after || {};
    return `${data.teamName || meta.teamName || "ทีม"} · ยอดโอน ${moneyMaybe(data.actualTransferAmount || 0)} บาท`;
  }
  return log.title || log.entityId || "รายการระบบ";
};

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, "audit-logs"), orderBy("createdAt", "desc"), limit(250)));
        if (mounted) setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error("load audit logs failed", error);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filtered = useMemo(() => filter === "all" ? logs : logs.filter((log) => log.entityType === filter), [logs, filter]);

  return (
    <div className="audit-page">
      <header className="audit-hero">
        <div><span>AUDIT LOG</span><h1>ประวัติการแก้ไขระบบ</h1><p>ตรวจว่าใครแก้รายการเงินจริง อะไร และเมื่อไหร่</p></div>
        <strong>{filtered.length} รายการ</strong>
      </header>
      <div className="audit-filters">
        {[['all','ทั้งหมด'],['extra-income','รายได้พิเศษ'],['press-fee','ค่ากด'],['settlement','รอบโอน']].map(([value,label]) => (
          <button key={value} className={filter === value ? "is-active" : ""} onClick={() => setFilter(value)}>{label}</button>
        ))}
      </div>
      {loading ? <div className="audit-empty">กำลังโหลดประวัติ…</div> : filtered.length === 0 ? <div className="audit-empty">ยังไม่มีประวัติการแก้ไข</div> : (
        <div className="audit-list">
          {filtered.map((log) => (
            <article className="audit-row" key={log.id}>
              <div className="audit-dot" />
              <div className="audit-main">
                <div className="audit-title"><strong>{ACTION_LABELS[log.action] || log.action}</strong><span>{fmtDate(log.createdAt)}</span></div>
                <p>{describe(log)}</p>
                <small>{log.actorName || log.actorEmail || log.actorUid}</small>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
